import { IScope } from "angular";
import { IGoal, SingleGoalSelectorInvokedBy } from "../models";
import { GoalService } from "../goalService";
import { UIErrorHandlingService } from "@gtmhub/error-handling";
import { GoalModalsService } from "../goalModalsService";
import { GoalLinkService, GoalLinkType } from "../goalLinkService";
import { GoalsChangedEventArgs, goalSelected, OkrEvents } from "@gtmhub/okrs";

// the implementation for the list with goals
// from which you can pick the one that will be supported
interface LinkSelectorGoalsParams {
  linkSelectorSessionId: string;
  linkSourceId: string;
}

interface LinkSelectorGoalsScope extends IScope {
  goals: IGoal[];
  indicators: Record<"gettingGoals", boolean>;
  updatingGoal: boolean;
  link(goal: IGoal, sourceId?: string): void;
  unlink(sourceId: string, targetId: string, linkType: GoalLinkType): void;
  linkGoal(goalId: string, sessionId: string): void;
}

export class LinkSelectorGoalsCtrl {
  public static $inject = ["$scope", "$stateParams", "GoalService", "GoalModalsService", "GoalLinkService", "UIErrorHandlingService"];
  private okrEvents: OkrEvents;

  constructor(
    private $scope: LinkSelectorGoalsScope,
    private $params: LinkSelectorGoalsParams,
    private goals: GoalService,
    private goalModalsService: GoalModalsService,
    private goalLinkService: GoalLinkService,
    private uiErrorHandlingService: UIErrorHandlingService
  ) {
    this.$scope.link = this.link;
    this.$scope.unlink = this.unlink;
    this.$scope.linkGoal = this.linkGoal;

    this.$scope.indicators = { gettingGoals: false };

    if (this.$params.linkSelectorSessionId) {
      this.loadGoals();
    }

    this.okrEvents = new OkrEvents(this.$scope);
    this.okrEvents.onGoalsChanged((args) => this.onGoalsChanged(args));
  }

  private currentGoalId: string;

  private loadGoals = (): void => {
    this.$scope.indicators.gettingGoals = true;
    const fields =
      "parentId,ownerIds,assignee{name,email,color,avatar,picture,type}," +
      "description,name,dateFrom,dateTo,attainment,sessionId," +
      "obfuscated,private,links{spec{supported,supporting}}";

    this.goals.getGoalsPaging(null, null, null, null, this.$params.linkSelectorSessionId, null, null, null, null, null, null, null, null, null, fields).then(
      (result) => {
        this.$scope.goals = result.items;
        this.$scope.indicators.gettingGoals = false;
      },
      (err) => this.uiErrorHandlingService.handleIndicator(err, { indicator: "gettingGoals" })
    );
  };

  private link = (goal: IGoal, sourceId = this.$params.linkSourceId): void => {
    this.goalLinkService.link(sourceId, "supporting", goal.id).then(
      () => {
        this.okrEvents.broadcastGoalsChanged("goalChanged");
      },
      (err) => this.uiErrorHandlingService.handleIndicator(err, { indicator: "linking" })
    );
  };

  private unlink = (sourceId: string, targetId: string, linkType: GoalLinkType): void => {
    this.$scope.updatingGoal = true;
    this.goalLinkService.unlink(sourceId, linkType, targetId).then(
      () => {
        this.$scope.updatingGoal = false;
        this.okrEvents.broadcastGoalsChanged("goalChanged");
      },
      (error) => this.uiErrorHandlingService.handleModal(error)
    );
  };

  private linkGoal = (goalId: string, sessionId: string): void => {
    this.currentGoalId = goalId;
    this.goalModalsService.openGoalSelectModal({ excludeSessionId: sessionId, invokedById: goalId, invokedBy: "LinkOKR" });
  };

  private onGoalsChanged = (args: GoalsChangedEventArgs): void => {
    if (args.reason === goalSelected) {
      this.onGoalSelected(args.detail.goal, args.detail.invokedById, args.detail.invokedBy);
    }
  };

  private onGoalSelected = (selectedGoal: IGoal, invokedById: string, invokedBy: SingleGoalSelectorInvokedBy): void => {
    if (this.currentGoalId === invokedById && invokedBy === "LinkOKR") {
      this.link(selectedGoal, this.currentGoalId);
    }
  };
}
